import React, { useState, useEffect } from 'react'
import { Form, Input, Button, Card, Typography, Spin, Tabs, Divider } from 'antd'
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'

const { Title, Text } = Typography
const { TabPane } = Tabs

interface LoginForm {
  email: string
  password: string
}

interface TwoFactorForm {
  twoFactorToken: string
  backupCode: string
}

const AdminLogin: React.FC = () => {
  const [form] = Form.useForm()
  const [twoFactorForm] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [showTwoFactor, setShowTwoFactor] = useState(false)
  const [loginCredentials, setLoginCredentials] = useState<LoginForm | null>(null)
  const [activeTab, setActiveTab] = useState('token')
  const { login, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Set page title and redirect if already authenticated
  useEffect(() => {
    document.title = 'OMRON Web Portal - Admin Login'
    
    if (isAuthenticated && !isLoading) {
      const from = location.state?.from?.pathname || '/admin/products'
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate, location])

  const handleSubmit = async (values: LoginForm) => {
    setLoading(true)
    try {
      const result = await login(values.email, values.password)
      if (result.success) {
        const from = location.state?.from?.pathname || '/admin/products'
        navigate(from, { replace: true })
      } else if (result.requiresTwoFactor) {
        setLoginCredentials(values)
        setShowTwoFactor(true)
      }
    } catch (error) {
      console.error('Login error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleTwoFactorSubmit = async (values: TwoFactorForm) => {
    if (!loginCredentials) return
    
    setLoading(true)
    try {
      const { twoFactorToken, backupCode } = values
      const result = await login(
        loginCredentials.email, 
        loginCredentials.password,
        activeTab === 'token' ? twoFactorToken : undefined,
        activeTab === 'backup' ? backupCode : undefined
      )
      
      if (result.success) {
        const from = location.state?.from?.pathname || '/admin/products'
        navigate(from, { replace: true })
      }
    } catch (error) {
      console.error('2FA verification error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setShowTwoFactor(false)
    setLoginCredentials(null)
    setActiveTab('token')
    twoFactorForm.resetFields()
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Title level={2} className="text-gray-900">
            OMRON Web Portal
          </Title>
          <Text className="text-gray-600">
            {showTwoFactor ? 'Two-Factor Authentication' : 'Sign in to your admin account'}
          </Text>
        </div>

        <Card className="shadow-lg border-0" style={{ borderRadius: '12px' }}>
          {!showTwoFactor ? (
            <Form
              form={form}
              name="admin-login"
              onFinish={handleSubmit}
              size="large"
              layout="vertical"
              requiredMark={false}
            >
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: 'Please enter your email address' },
                  { type: 'email', message: 'Please enter a valid email address' }
                ]}
              >
                <Input
                  prefix={<UserOutlined className="text-gray-400" />}
                  placeholder="Enter your email"
                  autoComplete="email"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Please enter your password' },
                  { min: 6, message: 'Password must be at least 6 characters' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="text-gray-400" />}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
              </Form.Item>

              <Form.Item className="mb-0">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="w-full"
                  loading={loading}
                  style={{ 
                    height: '48px',
                    fontSize: '16px',
                    fontWeight: 500,
                    borderRadius: '8px'
                  }}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </Form.Item>
            </Form>
          ) : (
            <div>
              <div className="text-center mb-6">
                <SafetyOutlined className="text-4xl text-blue-500 mb-2" />
                <Title level={4}>Verify your identity</Title>
                <Text className="text-gray-600">
                  Enter the 6-digit code from your authenticator app or use a backup code
                </Text>
              </div>

              <Tabs activeKey={activeTab} onChange={setActiveTab} centered>
                <TabPane tab="Authenticator Code" key="token">
                  <Form
                    form={twoFactorForm}
                    name="two-factor-token"
                    onFinish={handleTwoFactorSubmit}
                    size="large"
                    layout="vertical"
                    requiredMark={false}
                  >
                    <Form.Item
                      name="twoFactorToken"
                      label="6-digit code"
                      rules={[
                        { required: true, message: 'Please enter the 6-digit code' },
                        { pattern: /^\d{6}$/, message: 'Code must be exactly 6 digits' }
                      ]}
                    >
                      <Input
                        placeholder="000000"
                        maxLength={6}
                        style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '0.3em' }}
                      />
                    </Form.Item>

                    <Form.Item className="mb-4">
                      <Button
                        type="primary"
                        htmlType="submit"
                        className="w-full"
                        loading={loading}
                        style={{ 
                          height: '48px',
                          fontSize: '16px',
                          fontWeight: 500,
                          borderRadius: '8px'
                        }}
                      >
                        {loading ? 'Verifying...' : 'Verify Code'}
                      </Button>
                    </Form.Item>
                  </Form>
                </TabPane>

                <TabPane tab="Backup Code" key="backup">
                  <Form
                    form={twoFactorForm}
                    name="two-factor-backup"
                    onFinish={handleTwoFactorSubmit}
                    size="large"
                    layout="vertical"
                    requiredMark={false}
                  >
                    <Form.Item
                      name="backupCode"
                      label="8-character backup code"
                      rules={[
                        { required: true, message: 'Please enter the backup code' },
                        { pattern: /^[0-9A-F]{8}$/i, message: 'Backup code must be exactly 8 characters (0-9, A-F)' }
                      ]}
                    >
                      <Input
                        placeholder="XXXXXXXX"
                        maxLength={8}
                        style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '0.2em' }}
                        onChange={(e) => {
                          // Convert to uppercase
                          const value = e.target.value.toUpperCase()
                          twoFactorForm.setFieldValue('backupCode', value)
                        }}
                      />
                    </Form.Item>

                    <Form.Item className="mb-4">
                      <Button
                        type="primary"
                        htmlType="submit"
                        className="w-full"
                        loading={loading}
                        style={{ 
                          height: '48px',
                          fontSize: '16px',
                          fontWeight: 500,
                          borderRadius: '8px'
                        }}
                      >
                        {loading ? 'Verifying...' : 'Use Backup Code'}
                      </Button>
                    </Form.Item>
                  </Form>
                </TabPane>
              </Tabs>

              <Divider />
              
              <Button 
                type="link" 
                onClick={handleBackToLogin}
                className="w-full"
                disabled={loading}
              >
                ← Back to login
              </Button>
            </div>
          )}


        </Card>

        <div className="text-center">
          <Text className="text-gray-500 text-sm">
            OMRON HEALTHCARE Co., Ltd. © All Rights Reserved.
          </Text>
        </div>
      </div>
    </div>
  )
}

export default AdminLogin
